"use client";

import { useState, useEffect } from "react";
import Script from "next/script";
import { db } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export default function ContactPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;

  // Debug: Check environment variables on mount
  useEffect(() => {
    if (!projectId) {
      console.error("CRITICAL ERROR: NEXT_PUBLIC_FIREBASE_PROJECT_ID is not defined!");
      setErrorMessage("Configuration error: Firebase Project ID is missing.");
    } else {
      // Keep this block empty or add other non-logging logic if needed
    }
  }, [projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");
    
    try {
      if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        throw new Error("ReCAPTCHA not loaded");
      }

      // Wrap reCAPTCHA in a timeout promise
      const tokenPromise = window.grecaptcha.enterprise.execute(siteKey!, {
        action: "submit_contact_form",
      });
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Request timed out")), 10000)
      );

      const token = await Promise.race([tokenPromise, timeoutPromise]) as string;

      // Write to Firestore instead of calling Cloud Function
      const docData = {
        email,
        message,
        timestamp: serverTimestamp(),
        captchaToken: token, 
      };

      await Promise.race([
        addDoc(collection(db, "dbContact"), docData),
        timeoutPromise
      ]);
      
      setStatus("success");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="animate-in fade-in duration-500">
      <Script
        src={`https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`}
        strategy="afterInteractive"
      />
      
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="text-center mb-12">
          <h1
            className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.6s forwards",
            }}
          >
            Contact Us
          </h1>
          <p
            className="text-[#C4C8BA] opacity-0 blur-md translate-y-8"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.8s forwards",
            }}
          >
            Have a question or want to get in touch? Send us a message!
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          <div
            className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 sm:p-8 opacity-0 blur-md translate-y-8"
            style={{
              animation: "fadeSlideUnblur 1s ease-out 0.8s forwards",
            }}
          >
            {status === "success" ? (
              <div className="text-center py-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 mx-auto text-[#B0D18B] mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-2xl font-semibold text-[#E2E3D8] mb-2">Message Sent!</h3>
                <p className="text-[#C4C8BA]">
                  Thank you for contacting us. We will get back to you shortly.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-[#B0D18B] hover:text-[#CCEDA5] font-medium transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#E2E3D8] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#0C0F09] border border-[#8E9285]/20 text-[#E2E3D8] placeholder-[#57624A] focus:outline-none focus:border-[#B0D18B] focus:ring-1 focus:ring-[#B0D18B] transition-colors"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#E2E3D8] mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg bg-[#0C0F09] border border-[#8E9285]/20 text-[#E2E3D8] placeholder-[#57624A] focus:outline-none focus:border-[#B0D18B] focus:ring-1 focus:ring-[#B0D18B] transition-colors resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                {status === "error" && (
                  <div className="p-4 rounded-lg bg-red-900/20 border border-red-500/30 text-red-200 text-sm">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || !siteKey}
                  className="w-full flex items-center justify-center py-3 px-4 rounded-full bg-[#4B662C] hover:bg-[#587a33] text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
                >
                  {status === "loading" ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : !siteKey ? (
                    "ReCAPTCHA Key Missing"
                  ) : (
                    "Send Message"
                  )}
                </button>
                {!siteKey && (
                   <p className="text-xs text-center text-red-400 mt-2">
                     Please configure NEXT_PUBLIC_RECAPTCHA_SITE_KEY in your environment.
                   </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// Add types for global window object
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}
